import { useStore } from "@nanostores/preact";
import { useLocation } from "wouter-preact";
import { Icon } from "../icon/icon.tsx";
import { useTranslator } from "../../hooks/i18n.ts";
import { $isLogined, $isSubAccount, $organizationName, $username, logout } from "../../store/user_info.ts";
import { gotoLoginPage, onClickGotoHandler } from "../../router_utils.ts";
import { setDrawerNavOpen } from "../../store/browser_var.ts";
import type { IconName } from "../icon/feather_icon_name.d.ts";

type DrawerLocationItemProps = {
    href: string;
    icon?: IconName;
    name: string;
    active?: boolean;
    badge?: string;
};

const DrawerLocationItem = (props: DrawerLocationItemProps) => {
    return (
        <li
            data-href={props.href}
            onClick={onClickGotoHandler}
            class={props.active ? "active" : ""}
        >
            <Icon name={props.icon ?? "globe"} />
            <span>{props.name}</span>
            {props.badge ? <span class="farpov_nav_item_badge">{props.badge}</span> : null}
        </li>
    );
};

export const DrawerNavContent = () => {
    const username = useStore($username);
    const organizationName = useStore($organizationName);
    const isLogined = useStore($isLogined);
    const isSubAccount = useStore($isSubAccount);
    const [location, _setLocation] = useLocation();
    const t = useTranslator();

    const loginOrLogoutAction = async () => {
        if (isLogined) {
            await logout();
            setDrawerNavOpen(false);
            gotoLoginPage(location ?? "/");
        } else {
            setDrawerNavOpen(false);
            gotoLoginPage(location ?? "/");
        }
    };

    const isActive = (path: string) => {
        if (path === "/" || path === "/overview") {
            return location === "/" || location === "/overview";
        }
        return location?.startsWith(path);
    };

    return (
        <div class="farpov_drawer_nav_content">
            <div class="farpov_drawer_nav_user_info">
                {/* 
                <div class="farpov_drawer_nav_user_info_item">
                    <Icon name="settings" />
                </div>
                */}
                <div class="farpov_brand_icon" aria-hidden="true">
                    <Icon name="camera" />
                </div>
                <div class="farpov_brand_text">
                    <div class="farpov_brand_title">FARPOV</div>
                    <div class="farpov_brand_subtitle">TIME-LAPSE SYSTEM</div>
                </div>
            </div>

            <div class="farpov_drawer_nav_locations">
                <div class="farpov_nav_group_title">
                    {t({ zh: "管理", en: "Management", ar: "الإدارة" })}
                    {/* Debug Indicator for Sub Account */}
                    {isSubAccount && <span style={{ marginLeft: "0.5rem", fontSize: "0.7rem", color: "var(--pico-primary-background)", background: "var(--pico-primary-inverse)", padding: "0.1rem 0.3rem", borderRadius: "4px" }}>SUB</span>}
                </div>
                <ul>
                    <DrawerLocationItem
                        href="/overview"
                        icon="grid"
                        name={t({ zh: "相机总览", en: "Overview", ar: "نظرة عامة" })}
                        active={isActive("/overview") || location === "/"}
                    />
                    <DrawerLocationItem
                        href="/cameras"
                        icon="camera"
                        name={t({ zh: "相机管理", en: "Cameras", ar: "الكاميرات" })}
                        active={isActive("/cameras") || isActive("/camera/")}
                    />
                    {!isSubAccount && (
                        <DrawerLocationItem
                            href="/oss"
                            icon="cloud"
                            name={t({ zh: "存储管理", en: "Storage", ar: "التخزين" })}
                            active={isActive("/oss")}
                        />
                    )}
                    <DrawerLocationItem
                        href="/photos/0" 
                        icon="image"
                            name={t({ zh: "照片管理", en: "Pic", ar: "الصور" })}
                        active={isActive("/photos")}
                    />
                </ul>

                <div class="farpov_nav_group_title">{t({ zh: "智能工具箱", en: "Smart Toolbox", ar: "أدوات ذكية" })}</div>
                <ul>
                    <DrawerLocationItem
                        href="/ai_center"
                        icon="cpu"
                        name={t({ zh: "AI分析", en: "AI Analysis", ar: "تحليل الذكاء الاصطناعي" })}
                        badge="BETA"
                        active={isActive("/ai_center")}
                    />
                    <DrawerLocationItem
                        href="/make_video"
                        icon="film"
                        name={t({ zh: "视频汇报", en: "Video Report", ar: "تقرير فيديو" })}
                        active={isActive("/make_video")}
                    />
                    <DrawerLocationItem
                        href="/compare_photo"
                        icon="columns"
                        name={t({ zh: "照片对比", en: "Compare Photo", ar: "مقارنة الصور" })}
                        active={isActive("/compare_photo")}
                    />
                </ul>

                <div class="farpov_nav_group_title">{t({ zh: "系统", en: "System", ar: "النظام" })}</div>
                <ul>
                    <DrawerLocationItem
                        href="/bluetooth"
                        icon="bluetooth"
                        name={t({ zh: "安装调试", en: "Install & Debug", ar: "التثبيت والتصحيح" })}
                        active={isActive("/bluetooth")}
                    />
                    {!isSubAccount && (
                        <DrawerLocationItem
                            href="/settings"
                            icon="settings"
                            name={t({ zh: "用户设置", en: "Settings", ar: "الإعدادات" })}
                            active={isActive("/settings")}
                        />
                    )}
                    <li onClick={loginOrLogoutAction}>
                         <Icon name={isLogined ? "log-out" : "log-in"} />
                         <span>{isLogined ? t({ zh: "退出登录", en: "Log out", ar: "تسجيل الخروج" }) : t({ zh: "登录", en: "Log in", ar: "تسجيل الدخول" })}</span>
                    </li>
                </ul>
            </div>
            
            {isLogined && (
                <div class="farpov_drawer_footer_user">
                    <div class="user-avatar">
                        <Icon name="user" />
                    </div>
                    <div class="user-info">
                        <span class="username">{username}</span>
                        <span class="role">{organizationName}</span>
                    </div>
                </div>
            )}
        </div>
    );
};
